import z from 'zod'

export const affiliateStatuses = ['active', 'paused', 'disabled', 'archived'] as const
export type AffiliateStatus = (typeof affiliateStatuses)[number]

/**
 * Stored shape in the `affiliate` table — one item per flagged user.
 *
 * The affiliate is a PERSON (linked by Clerk `userId`, the `userId` GSI), not an org.
 * `promoCode` (the `promoCode` GSI) is the human-facing referral code; the Stripe
 * `coupon`/`promotionCode` objects are created up front and their ids stored here.
 */
export interface AffiliateSchema {
    affiliateId: string
    userId: string // Clerk user id — GSI `userId`
    name: string
    email: string
    status: AffiliateStatus

    // Customer-facing discount the affiliate's referral link grants.
    discountPercent: number // e.g. 10
    discountDurationMonths: number // customer-facing months (e.g. 6); the Stripe coupon is created at +1 to absorb the free trial

    // Commission the affiliate earns on each referred customer's net collected revenue.
    commissionPercent: number // e.g. 20
    commissionDurationMonths: number // window length, e.g. 12

    stripeCouponId?: string
    stripePromotionCodeId?: string
    promoCode: string // GSI `promoCode`
    referralLink?: string
    // Optional cap on total redemptions of this code; persisted so it survives coupon rotation.
    maxRedemptions?: number

    // Stripe Connect Express (payouts).
    stripeConnectAccountId?: string
    connectOnboardingComplete: boolean
    // Set when a bank payout (connected account → bank) fails — the transferred funds sit
    // safely in the affiliate's Stripe balance, so this is a "fix your bank details" alert,
    // NOT a balance adjustment. Cleared when a later payout succeeds.
    payoutFailed?: boolean
    payoutFailureReason?: string
    payoutFailedAt?: number

    // AU compliance — required before payouts run.
    abn?: string
    agreementVersion?: string
    agreementAcceptedAt?: number

    createdAt: number
    updatedAt: number
    createdByAdminId: string
}

const promoCodeRegex = /^[A-Z0-9]{3,40}$/

const promoCodeField = z
    .string()
    .transform(v => v.trim().toUpperCase())
    .refine(v => promoCodeRegex.test(v), 'Code must be 3–40 letters or numbers')

// Australian Business Number — 11 digits. Stored normalised (digits only). Required before
// payouts run (the affiliate manager enters it when creating/editing an affiliate).
const abnField = z
    .string()
    .transform(v => v.replace(/\s+/g, ''))
    .refine(v => /^\d{11}$/.test(v), 'ABN must be 11 digits')

export const CreateAffiliateInputSchema = z.object({
    userId: z.string().min(1, 'User is required'),
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('A valid email is required'),
    promoCode: promoCodeField,
    discountPercent: z.number().min(0).max(100).default(10),
    discountDurationMonths: z.number().int().min(1).max(36).default(6),
    commissionPercent: z.number().min(0).max(100).default(20),
    commissionDurationMonths: z.number().int().min(1).max(60).default(12),
    // Required up front — payouts can't run without it (ATO no-ABN withholding rule).
    abn: abnField,
    // Optional cap on how many customers can redeem this affiliate's code.
    maxRedemptions: z.number().int().min(1).optional(),
}).strict()
export type CreateAffiliateInput = z.infer<typeof CreateAffiliateInputSchema>

export const UpdateAffiliateInputSchema = z.object({
    affiliateId: z.string().min(1),
    name: z.string().min(1).optional(),
    status: z.enum(affiliateStatuses).optional(),
    discountPercent: z.number().min(0).max(100).optional(),
    discountDurationMonths: z.number().int().min(1).max(36).optional(),
    commissionPercent: z.number().min(0).max(100).optional(),
    commissionDurationMonths: z.number().int().min(1).max(60).optional(),
    abn: abnField.optional(),
}).strict()
export type UpdateAffiliateInput = z.infer<typeof UpdateAffiliateInputSchema>

/** Aggregated earnings for an affiliate, derived from the commission ledger. */
export interface AffiliateEarnings {
    affiliateId: string
    /** Net commission owed but not yet paid out (current balance = sum of all ledger entries). */
    unpaidCents: number
    /** Total commission already transferred to the affiliate. */
    paidToDateCents: number
    /** Gross commission earned over all time (unpaid + paid). */
    lifetimeCents: number
    currency: string
    lastPayoutAt?: number
}
